import { Classes } from "@blueprintjs/core";
import { isAfter, isBefore } from "date-fns";
import { useNavigate } from "react-router";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box } from "@/components/layout/flexbox";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { ProgramRoleplayCard } from "@/components/pages_logged_in/program/components/ProgramRoleplayCard";
import { ParticipantComponentStatistic } from "@/components/pages_logged_in/program/components/program-component/ParticipantComponentStatistic";
import { canViewComponentStatistic } from "@/components/pages_logged_in/program/components/utils";
import { Button } from "@/components/ui/button";
import { PanelProps } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

export function ProgramRoleplaysItemPanel({
  component,
  program,
  role = "participant",
  large = false,
  view = "list",
}: PanelProps) {
  const navigate = useNavigate();
  const {
    participantGuidance,
    targetStartDate,
    targetEndDate,
    closedAt,
    status,
  } = component || {};

  const roleplayScenarios = ensureNonNullArray(component?.roleplayScenarios);

  const isProgramLead = role === "lead";

  const canViewStatistic = canViewComponentStatistic(component, isProgramLead);

  const metaData = buildComponentMetadata({
    component,
    isProgramLead,
  });

  const dateTargetStartDate = targetStartDate
    ? new Date(targetStartDate)
    : null;
  const dateTargetEndDate = targetEndDate ? new Date(targetEndDate) : null;

  const canDoRoleplay =
    role === "participant" &&
    !closedAt &&
    dateTargetStartDate &&
    dateTargetEndDate &&
    isBefore(dateTargetStartDate, new Date()) &&
    isAfter(dateTargetEndDate, new Date());

  const shouldShowRoleplay = roleplayScenarios?.length || isProgramLead;

  if (view === "feed") {
    return (
      <div className="xs:p-3.5 xs:rounded-md xs:gap-3 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-4 md:rounded-lg md:p-6">
        <div className="text-lg font-semibold text-blue-800">
          You have {roleplayScenarios?.length} assigned scenarios to complete
        </div>
        <div>
          <Button
            className="xs:px-7 xs:py-2.5 md:px-12 md:py-3"
            onClick={() =>
              navigate(`/programs/${program.id}#component_id_${component.id}`)
            }
            variant="solid"
            intent="primary"
          >
            <span className="xs:text-sm font-semibold md:text-base">
              View Scenarios
            </span>
          </Button>
        </div>
      </div>
    );
  }

  if (!shouldShowRoleplay) {
    return <></>;
  }

  return (
    <Box className="xs:gap-3.5 flex flex-col md:gap-4 empty:hidden">
      {canViewStatistic && (
        <ParticipantComponentStatistic
          component={component}
          program={program}
          isProgramLead={isProgramLead}
        />
      )}

      {status !== "Completed" && !canViewStatistic && !!participantGuidance && (
        <div className="flex flex-col gap-2">
          <div className="text-subtext text-xs font-semibold">
            {metaData.isSurvey ? "GUIDANCE" : ""}
          </div>
          <div className={Classes.RUNNING_TEXT}>
            <RichContentRenderer content={participantGuidance} />
          </div>
        </div>
      )}

      <ProgramRoleplayCard
        roleplayScenarios={roleplayScenarios}
        canDoRoleplay={canDoRoleplay ? metaData.isEligible : undefined}
        hideRoleplayRank={role !== "participant"}
        large={large}
        isProgramLead={isProgramLead}
        isProgramClosed={!!closedAt}
        minimumRank={component.scenarioRankMin}
        minimumAttempts={component.scenarioAttemptMin}
        maximumAttempts={component.scenarioAttemptMax}
        scenarioRequiredNum={component.scenarioRequiredNum}
        hasCompletedPrerequisites={
          component.prerequisiteComponent
            ? component.prerequisiteComponent?.status === "Completed"
            : true
        }
      />
    </Box>
  );
}
